import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { SelectSingle } from "../../SelectSingle";
import { Users, Loader2, X } from "lucide-react";
import { api } from "../../../services/api";
import { GrupoTipo } from "../../../types/grupos";

interface Grupo {
  id: string;
  grupo: string;
  descricao: string;
  img: string;
  banner?: string | null;
  status: string;
  id_tipo: string;
  meta_pontuacao?: number | null;
  criado_em: string;
  alterado_em: string;
  grupo_tipo_id?: string; // Para compatibilidade
}

interface FormValues {
  grupo: string;
  descricao: string;
  img: string;
  banner: string;
  status: string;
  grupo_tipo_id: string;
  meta_pontuacao: string;
}

interface GrupoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Grupo | null;
  onSave?: () => void;
  editingGrupo?: Grupo | null; // Manter para compatibilidade
  onSuccess?: () => void;
  gruposTipos?: GrupoTipo[];
}

const validationSchema = Yup.object().shape({
  grupo: Yup.string().required("Nome do grupo é obrigatório"),
  descricao: Yup.string().required("Descrição é obrigatória"),
  img: Yup.string()
    .url("Deve ser uma URL válida")
    .required("URL da imagem é obrigatória"),
  banner: Yup.string()
    .url("Deve ser uma URL válida")
    .optional()
    .nullable(),
  status: Yup.string().required("Status é obrigatório"),
  grupo_tipo_id: Yup.string().required("Tipo do grupo é obrigatório"),
});

export function GrupoModal({
  isOpen,
  onClose,
  initialData = null,
  onSave,
  editingGrupo = null,
  onSuccess,
  gruposTipos = []
}: GrupoModalProps) {
  // Usar initialData se fornecido, senão usar editingGrupo para compatibilidade
  const grupoData = initialData || editingGrupo;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormValues>({
    grupo: "",
    descricao: "",
    img: "",
    banner: "",
    status: "ativo",
    grupo_tipo_id: "",
    meta_pontuacao: "",
  });
  const [errors, setErrors] = useState<Partial<FormValues>>({});

  useEffect(() => {
    if (grupoData) {
      setFormData({
        grupo: grupoData.grupo,
        descricao: grupoData.descricao,
        img: grupoData.img,
        banner: grupoData.banner || "",
        status: grupoData.status === "A" ? "ativo" : "inativo",
        grupo_tipo_id: grupoData.id_tipo || grupoData.grupo_tipo_id || "",
        meta_pontuacao: grupoData.meta_pontuacao?.toString() || "",
      });
    } else {
      setFormData({
        grupo: "",
        descricao: "",
        img: "",
        banner: "",
        status: "ativo",
        grupo_tipo_id: "",
        meta_pontuacao: "",
      });
    }
    setErrors({});
  }, [grupoData, isOpen]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: Partial<FormValues> = {};
      error.inner.forEach((err: any) => {
        validationErrors[err.path as keyof FormValues] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      if (grupoData) {
        // Edição
        await api.put(`/grupos/${grupoData.id}`, {
          grupo: formData.grupo,
          descricao: formData.descricao,
          img: formData.img,
          banner: formData.banner || null,
          status: formData.status === "ativo" ? "A" : "I",
          id_tipo: formData.grupo_tipo_id,
          meta_pontuacao: formData.meta_pontuacao ? parseInt(formData.meta_pontuacao) : null,
        });
        enqueueSnackbar("Grupo atualizado com sucesso!", { variant: "success" });
      } else {
        // Criação
        await api.post("/grupos", {
          ...formData,
          banner: formData.banner || null,
          status: formData.status === "ativo" ? "A" : "I",
          id_tipo: formData.grupo_tipo_id,
          meta_pontuacao: formData.meta_pontuacao ? parseInt(formData.meta_pontuacao) : null,
        });
        enqueueSnackbar("Grupo criado com sucesso!", { variant: "success" });
      }

      // Chamar callback de sucesso
      if (onSave) {
        onSave();
      } else if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar grupo", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ grupo: "", descricao: "", img: "", banner: "", status: "ativo", grupo_tipo_id: "", meta_pontuacao: "" });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {grupoData ? "Editar Grupo" : "Novo Grupo"}
            </CardTitle>
            <CardDescription>
              {grupoData
                ? "Atualize as informações do grupo"
                : "Preencha os dados para criar um novo grupo"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="grupo">Nome do Grupo</Label>
              <InputText
                id="grupo"
                name="grupo"
                title=""
                value={formData.grupo}
                onChange={(e) => setFormData({ ...formData, grupo: e.target.value })}
                placeholder="Digite o nome do grupo"
                error={errors.grupo}
                className="mt-1"
                disabled={loading}
              />
            </div>

            <div>
              <SelectSingle
                name="grupo_tipo_id"
                title="Tipo do Grupo"
                value={formData.grupo_tipo_id}
                onChange={(e) => setFormData({ ...formData, grupo_tipo_id: e.target.value })}
                disabled={loading}
                error={errors.grupo_tipo_id}
                required
              >
                <option value="">Selecione o tipo do grupo</option>
                {gruposTipos.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nome}
                  </option>
                ))}
              </SelectSingle>
            </div>

            <div>
              <Label htmlFor="descricao">Descrição</Label>
              <textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Digite a descrição do grupo"
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                rows={3}
                disabled={loading}
              />
              {errors.descricao && (
                <p className="text-sm text-destructive mt-1">{errors.descricao}</p>
              )}
            </div>

            <div>
              <Label htmlFor="img">URL Emblema</Label>
              <InputText
                id="img"
                name="img"
                title=""
                value={formData.img}
                onChange={(e) => setFormData({ ...formData, img: e.target.value })}
                placeholder="https://exemplo.com/imagem.jpg"
                className="mt-1"
                error={errors.img}
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="banner">Banner</Label>
              <InputText
                id="banner"
                name="banner"
                title=""
                value={formData.banner}
                onChange={(e) => setFormData({ ...formData, banner: e.target.value })}
                placeholder="https://exemplo.com/banner.jpg (1860 x 270 px)"
                className="mt-1"
                error={errors.banner}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Imagem exibida na LP. Tamanho recomendado: 1860 × 270 px.
              </p>
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={loading}
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>

            <div>
              <Label htmlFor="meta_pontuacao">Meta de Pontuação (Fechamento)</Label>
              <InputText
                id="meta_pontuacao"
                name="meta_pontuacao"
                title=""
                type="number"
                value={formData.meta_pontuacao}
                onChange={(e) => setFormData({ ...formData, meta_pontuacao: e.target.value })}
                placeholder="Ex: 100 (deixe vazio para desativar)"
                className="mt-1"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pontuação mínima para manter status Ativo no fechamento. Semi-Ausente = 50% da meta.
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {grupoData ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  grupoData ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
